<template>
  <!-- The Better Auth sign-in page (modes "both" and "identity"). Left: the
       tenant's field panel. Right: the form slot, the trust line, the help and
       legal links, and the language switch. Everything comes from the resolved
       tenant; nothing here names a tenant. -->
  <div
    class="si-page"
    :class="{ 'si-motion': animate, 'si-page--metaship': metashipPage, 'si-page--dark': metashipPage && darkMode }"
    data-test="signin-split"
  >
    <!-- metaship.ai: the landing-page hero ground (aurora washes + the same world map) runs under
         BOTH halves, so the page reads as one surface, not two. Decoration only. -->
    <div v-if="metashipPage" class="si-map" aria-hidden="true" data-test="signin-map">
      <img :src="worldMapUrl" alt="" />
    </div>
    <FieldPanel
      :brand="brand"
      :branding="branding"
      :deep="deep"
      :eyebrow="t('auth.identity.panel.eyebrow')"
      :headline="headline"
      :lede="lede"
      :features="features"
      :inside-label="t('auth.identity.panel.inside')"
      :footnote="t('auth.identity.panel.copyright', { year, name: brand })"
      :scene="loginPage?.panelScene === 'network' ? 'network' : 'contours'"
      :network-nodes="loginPage?.panelNodes ?? []"
      :light="metashipPage && !darkMode"
      :bare="metashipPage"
    />

    <main class="si-main">
      <button
        type="button"
        class="si-main__theme"
        :aria-label="darkMode ? 'Switch to light theme' : 'Switch to dark theme'"
        :title="darkMode ? 'Light theme' : 'Dark theme'"
        @click="onToggleTheme"
      >
        <v-icon size="18">{{ darkMode ? "light_mode" : "dark_mode" }}</v-icon>
      </button>

      <div class="si-card">
        <div class="si-card__head">
          <h2 class="si-card__title">{{ t("auth.identity.title", { name: brand }) }}</h2>
          <p class="si-card__subtitle">{{ t("auth.identity.subtitle") }}</p>
        </div>

        <slot />

        <p class="si-trust">
          <svg class="si-trust__icon" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 3 4 6v6c0 4.5 3.4 8.4 8 9 4.6-.6 8-4.5 8-9V6z" />
            <path d="m9 12 2 2 4-4" />
          </svg>
          <I18nT keypath="auth.identity.trust" tag="span">
            <template #host><strong data-test="trust-host">{{ host }}</strong></template>
          </I18nT>
        </p>

        <footer v-if="links.length || poweredBy" class="si-links">
          <nav v-if="links.length" class="si-links__row" :aria-label="t('footer.helpLegal')">
            <a
              v-for="link in links"
              :key="link.key"
              :href="link.href"
              :data-test="`link-${link.key}`"
              :target="link.href.startsWith('mailto:') ? undefined : '_blank'"
              rel="noopener noreferrer"
              @click="onLinkClick($event, link)"
              >{{ t(FOOTER_KEYS[link.key]) }}</a>
          </nav>
          <span v-if="poweredBy" class="si-links__credit" data-test="powered-by">{{ t("footer.poweredBy") }}</span>
        </footer>

        <LanguageSwitch />
      </div>
    </main>

    <!-- Terms / Privacy PDFs open in the shared @pdfviewer, not a new tab. -->
    <v-dialog v-model="docOpen" max-width="1000" :fullscreen="narrow" scrollable>
      <v-card class="si-doc" data-test="signin-doc-dialog">
        <div class="si-doc__bar">
          <span class="si-doc__title">{{ docTitle }}</span>
          <button type="button" class="si-doc__close" :aria-label="t('common.close')" @click="docOpen = false">
            <v-icon size="20">close</v-icon>
          </button>
        </div>
        <div class="si-doc__pane">
          <PdfViewer
            v-if="docOpen && docUrl"
            :source="docUrl"
            :page="docPage"
            :title="docTitle"
            :download-name="`${brand}-${docTitle}.pdf`"
          />
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script lang="ts">
import { computed, defineAsyncComponent, defineComponent, ref } from "vue";
import { I18nT, useI18n } from "vue-i18n";
import { useSessionStore } from "@/stores/session";
import { hasOwnIdentity } from "@/tenant/branding";
import { isPdfUrl, preAuthLinks, showPoweredBy, type PreAuthLink } from "@/tenant/loginLinks";
import { useThemeToggle } from "@/shell/useThemeToggle";
import { useTheme } from "vuetify";
import { isMetashipHandoff, saveMetashipDark } from "@/handoff/metashipSignIn";
import { applyDocumentTheme } from "@/state/globalState";
import worldMapUrl from "@/assets/world-map.svg";
import { LANGUAGE_OPTIONS } from "@/i18n/languages";
import FieldPanel, { type PanelFeature } from "./FieldPanel.vue";
import LanguageSwitch from "./LanguageSwitch.vue";
import { panelBackground, prefersReducedMotion } from "./panelColours";

const FOOTER_KEYS = { support: "footer.needHelp", terms: "footer.terms", privacy: "footer.privacy" } as const;
/** At most this many feature lines — a hint of what is inside, not a menu. */
const MAX_FEATURES = 3;

export default defineComponent({
  name: "SignInSplit",
  components: {
    FieldPanel,
    LanguageSwitch,
    I18nT,
    // Lazy: pdf.js loads only when a person opens Terms or Privacy.
    PdfViewer: defineAsyncComponent(() => import("@pdfviewer").then((m) => m.PdfViewer)),
  },
  setup() {
    const { t } = useI18n();
    const session = useSessionStore();
    const { darkMode, toggleTheme } = useThemeToggle();
    // metaship.ai sign-in page: the landing-page ground and map, and the person's theme, shared with
    // metaship.ai/ and /portal (resolveMetashipDark). Was (briefly, 2026-11-01): always light.
    const metashipPage = isMetashipHandoff();
    const theme = useTheme();
    function onToggleTheme(): void {
      if (!metashipPage) return toggleTheme();
      const next = !darkMode.value;
      saveMetashipDark(next);
      theme.change(next ? "dark" : "light");
      applyDocumentTheme(next);
    }

    const brand = computed(() => session.tenant?.name || "Metaship");
    const branding = computed(() => session.tenant?.branding ?? null);
    const homePage = computed(() => session.tenant?.homePage ?? null);
    const loginPage = computed(() => session.tenant?.loginPage ?? null);

    const deep = computed(() => panelBackground(branding.value?.colours?.secondary));
    // The tenant's own welcome copy (App Home settings) wins over the generic text.
    // A welcome title that only repeats the app name is skipped: the brand row
    // and the form title already show the name (Boer showed "Boer" 3 times).
    const headline = computed(() => {
      const own = homePage.value?.welcomeTitle?.trim();
      return own && own.toLowerCase() !== brand.value.trim().toLowerCase() ? own : t("auth.identity.panel.headline");
    });
    const lede = computed(() => homePage.value?.welcomeText?.trim() || t("auth.identity.panel.lede"));
    // Old: tile titles only, as chips. Replaced by feature lines that also carry
    // the tile icon and description, with generic facts when no tile is left.
    // const chips = computed(() => { ...tiles.map((tile) => tile.title.trim())... });
    const features = computed<PanelFeature[]>(() => {
      const name = brand.value.trim().toLowerCase();
      const tiles = homePage.value?.enabled
        ? (homePage.value.tiles ?? [])
            .filter((tile) => tile.title.trim() && tile.title.trim().toLowerCase() !== name)
            .map((tile) => ({ icon: tile.icon || "apps", title: tile.title.trim(), description: tile.description?.trim() || null }))
        : [];
      if (tiles.length) return tiles.slice(0, MAX_FEATURES);
      return [
        { icon: "devices", title: t("auth.identity.panel.devicesTitle"), description: t("auth.identity.panel.devicesText") },
        {
          icon: "translate",
          title: t("auth.identity.panel.languagesTitle"),
          description: t("auth.identity.panel.languagesText", { count: LANGUAGE_OPTIONS.length }),
        },
        { icon: "verified_user", title: t("auth.identity.panel.secureTitle"), description: t("auth.identity.panel.secureText") },
      ];
    });
    const year = new Date().getFullYear();

    const host = window.location.host;
    const links = computed(() => preAuthLinks(loginPage.value));
    // An own-brand app never shows the Metaship name (DEV-925).
    const poweredBy = computed(
      () =>
        showPoweredBy(loginPage.value, brand.value) &&
        !hasOwnIdentity(session.tenant?.kind, branding.value),
    );
    const animate = !prefersReducedMotion();

    const docOpen = ref(false);
    const docUrl = ref("");
    const docTitle = ref("");
    const docPage = ref(1);
    const narrow = typeof window !== "undefined" && window.matchMedia?.("(max-width: 600px)").matches;
    /** A PDF link opens in the viewer dialog; any other link (mailto, a web page) behaves as a link. */
    function onLinkClick(event: MouseEvent, link: PreAuthLink): void {
      if (link.key === "support" || !isPdfUrl(link.href)) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
      event.preventDefault();
      // `…pdf#page=7` (Privacy = section 11 of the terms): open the viewer at that page.
      // The viewer fetches the file itself, so the fragment is not part of its source.
      const hashAt = link.href.indexOf("#");
      const file = hashAt < 0 ? link.href : link.href.slice(0, hashAt);
      const fragment = hashAt < 0 ? "" : link.href.slice(hashAt + 1);
      const page = Number(new URLSearchParams(fragment).get("page"));
      docUrl.value = file;
      docPage.value = Number.isInteger(page) && page > 0 ? page : 1;
      docTitle.value = t(FOOTER_KEYS[link.key]);
      docOpen.value = true;
    }

    return {
      t,
      darkMode,
      metashipPage,
      onToggleTheme,
      worldMapUrl,
      toggleTheme,
      brand,
      branding,
      deep,
      headline,
      lede,
      features,
      year,
      host,
      links,
      poweredBy,
      animate,
      docOpen,
      docUrl,
      docTitle,
      docPage,
      narrow,
      onLinkClick,
      loginPage,
      FOOTER_KEYS,
    };
  },
});
</script>

<style scoped>
.si-page {
  --si-mono: ui-monospace, SFMono-Regular, Menlo, monospace;
  display: flex;
  min-height: 100vh;
  min-height: 100dvh;
  width: 100%;
  overflow-x: hidden;
  background: rgb(var(--v-theme-background));
  color: var(--sh-text);
}
/* metaship.ai: the landing-page hero ground (website/index.html .hero), light and dark. */
.si-page--metaship {
  position: relative;
  --glass-bg: rgba(247, 252, 255, 0.72);
  --glass-edge: rgba(255, 255, 255, 0.92);
  --glass-sheen: rgba(255, 255, 255, 0.85);
  --glass-shadow: 0 8px 30px rgba(32, 70, 103, 0.12), 0 30px 80px rgba(23, 42, 66, 0.12);
  background:
    radial-gradient(78% 62% at 76% 14%, rgba(158, 210, 242, 0.42), transparent 66%),
    radial-gradient(62% 68% at 4% 92%, rgba(100, 158, 206, 0.2), transparent 64%),
    radial-gradient(46% 40% at 40% -8%, rgba(207, 230, 247, 0.5), transparent 70%),
    #ffffff;
}
.si-page--metaship.si-page--dark {
  --glass-bg: rgba(16, 25, 39, 0.62);
  --glass-edge: rgba(180, 216, 246, 0.24);
  --glass-sheen: rgba(220, 240, 255, 0.16);
  --glass-shadow: 0 8px 30px rgba(0, 0, 0, 0.2), 0 30px 80px rgba(0, 0, 0, 0.45);
  background:
    radial-gradient(78% 62% at 76% 14%, rgba(79, 148, 209, 0.3), transparent 66%),
    radial-gradient(62% 68% at 4% 92%, rgba(46, 111, 174, 0.24), transparent 64%),
    radial-gradient(46% 40% at 40% -8%, rgba(111, 176, 230, 0.2), transparent 70%),
    #070a10;
}
.si-map {
  position: absolute;
  inset: 0;
  pointer-events: none;
  opacity: 0.32;
  /* The website's mask, centred wider so the atlas also runs under the glass card. */
  -webkit-mask-image: radial-gradient(110% 100% at 54% 48%, #000 26%, rgba(0, 0, 0, 0.4) 58%, transparent 82%);
  mask-image: radial-gradient(110% 100% at 54% 48%, #000 26%, rgba(0, 0, 0, 0.4) 58%, transparent 82%);
}
.si-page--dark .si-map {
  opacity: 0.55;
}
.si-map img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.si-page--metaship > :not(.si-map) {
  position: relative;
  z-index: 1;
}
/* A soft brand glow behind the card: something for the glass to bend. */
.si-page--metaship .si-main::before {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  width: min(560px, 90%);
  aspect-ratio: 1;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  background: radial-gradient(closest-side, rgba(100, 158, 206, 0.35), transparent);
  pointer-events: none;
}
/* Liquid glass: the landing-page header recipe (website .hdr__in), sized for a card. */
.si-page--metaship .si-card {
  position: relative;
  padding: 36px;
  border: 1px solid var(--glass-edge);
  border-radius: 28px;
  background:
    linear-gradient(135deg, var(--glass-sheen), transparent 36%, rgba(111, 176, 230, 0.08)),
    var(--glass-bg);
  -webkit-backdrop-filter: blur(24px) saturate(160%);
  backdrop-filter: blur(24px) saturate(160%);
  box-shadow:
    inset 0 1px 0 var(--glass-sheen),
    inset 0 -1px 0 rgba(120, 170, 210, 0.12),
    var(--glass-shadow);
}
@media (max-width: 1039px) {
  .si-map {
    display: none;
  }
}
@media (max-width: 900px) {
  .si-page--metaship .si-card {
    padding: 24px 20px;
    border-radius: 22px;
  }
}
.si-main {
  position: relative;
  /* 56 : 44 with the field panel, side by side above 900px (no wrap gap at 901-999px). */
  flex: 44 1 0;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px max(24px, env(safe-area-inset-right, 0px)) max(48px, env(safe-area-inset-bottom, 0px))
    max(24px, env(safe-area-inset-left, 0px));
  box-sizing: border-box;
}
.si-main__theme {
  position: absolute;
  top: max(12px, env(safe-area-inset-top, 0px));
  right: max(12px, env(safe-area-inset-right, 0px));
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: var(--sh-text-60);
  cursor: pointer;
}
.si-main__theme:hover {
  background: var(--sh-hover);
}
.si-main__theme:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 2px;
}
.si-card {
  width: 100%;
  max-width: 420px;
  display: flex;
  flex-direction: column;
  gap: 22px;
}
.si-motion .si-card {
  animation: si-rise 0.6s cubic-bezier(0.2, 0.8, 0.2, 1) both;
}
.si-card__head {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.si-card__title {
  margin: 0;
  font-size: 32px;
  font-weight: 700;
  letter-spacing: -0.8px;
  line-height: 1.15;
  overflow-wrap: anywhere;
}
.si-card__subtitle {
  margin: 0;
  font-size: 15px;
  color: var(--sh-text-60);
}
.si-trust {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
  padding: 12px 14px;
  border-radius: 12px;
  background: rgba(var(--v-theme-primary), 0.1);
  font-size: 12px;
  line-height: 1.5;
  overflow-wrap: anywhere;
}
.si-trust__icon {
  flex: 0 0 auto;
  width: 16px;
  height: 16px;
  fill: none;
  stroke: rgb(var(--v-theme-primary));
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
}
.si-links {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  /* Was space-between (links left-aligned under the card). Centred, as on the landing page. */
  justify-content: center;
  gap: 6px 16px;
  font-size: 12px;
  color: var(--sh-text-60);
}
.si-links__row {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 4px 0;
}
/* A short hairline between the links. */
.si-links__row a + a::before {
  content: "";
  display: inline-block;
  width: 1px;
  height: 14px;
  margin: 0 16px;
  background: currentColor;
  opacity: 0.35;
}
.si-doc {
  display: flex;
  flex-direction: column;
  height: 88vh;
}
.si-doc__bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px 10px 20px;
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.08);
}
.si-doc__title {
  font-size: 15px;
  font-weight: 600;
}
.si-doc__close {
  display: grid;
  place-items: center;
  width: 40px;
  height: 40px;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: inherit;
  cursor: pointer;
}
.si-doc__close:hover {
  background: rgba(var(--v-theme-on-surface), 0.08);
}
/* @pdfviewer needs a definite height on its host pane. */
.si-doc__pane {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
.si-links__row a {
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  color: var(--sh-text-60);
  text-decoration: none;
}
.si-links__row a:hover {
  color: var(--sh-text);
  text-decoration: underline;
}
.si-links__row a:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 2px;
}
@keyframes si-rise {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

@media (max-width: 900px) {
  .si-page {
    flex-direction: column;
  }
  .si-main {
    flex: 1 0 auto;
    align-items: flex-start;
    /* Was 28px: the glass card started under the 44px theme toggle and painted over it. */
    padding: 60px max(16px, env(safe-area-inset-right, 0px)) max(40px, env(safe-area-inset-bottom, 0px))
      max(16px, env(safe-area-inset-left, 0px));
  }
  .si-main__theme {
    /* The band above holds the brand; the toggle sits in the form area's corner, above the card. */
    top: 8px;
    z-index: 2;
  }
  .si-card__title {
    font-size: 26px;
    padding-right: 44px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .si-card {
    animation: none !important;
  }
}
</style>
